import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as config from "../constants/config";
import * as reducerHelper from "../helpers/reducerHelper";
import * as modelHelper from "../helpers/modelHelper";
import * as getterHelper from "../helpers/getterHelper";
import * as searchPanelService from "../services/searchPanelService";

const initialState = {
    type: componentTypes.SEARCH_PANEL_DEFAULT,
    init: false,
    activeSelect: "",
    productNameInput: "",
    fandeckNameInput: "",
    formulaNameInput: "",
    formulaCodeInput: "",
    baseNameInput: "",
    bippNameInput: "",
    productNameFilter: "",
    fandeckNameFilter: "",
    formulaNameFilter: "",
    fandeckListOffset: config.MAX_FANDECK_LIMIT,
    formulaListOffset: config.MAX_FORMULA_LIMIT,
    formulaCodeListOffset: config.MAX_FORMULA_LIMIT,
    baseListOffset: config.MAX_BASE_LIMIT,
    bippListOffset: config.MAX_BIPP_LIMIT,
    formulaListOrder: null,
    formulaListAsc: true,
    productList: [],
    linearProductList: [],
    fandeckList: [],
    linearFandeckList: [],
    formulaList: [],
    formulaCodeList: [],
    baseList: [],
    bippList: [],
    local: false,
    refresh: null,
    reload: false
};

/**
 * Structure of object from searchPanel store:
 * - type {String}                  - search panel type
 * - activeSelect {String}          - active select box
 * - productNameInput {String}      - text for product name input
 * - fandeckNameInput {String}      - text for fandeck name input
 * - formulaNameInput {String}      - text for formula name input
 * - formulaCodeInput {String}      - text for formula code input
 * - baseNameInput {String}         - text for base name input
 * - bippNameInput {String}         - text for bipp name input
 * - productNameFilter {String}     - active filter text for product list
 * - fandeckNameFilter {String}     - active filter text for fandeck list
 * - formulaNameFilter {String}     - active filter text for formula list
 * - fandeckListOffset {Number}     - offset of fandeck list
 * - formulaListOffset {Number}     - offset of formula list
 * - formulaCodeListOffset {Number} - offset of formula code list
 * - baseListOffset {Number}        - offset of base list
 * - bippListOffset {Number}        - offset of bipp list
 * - formulaListOrder {String}      - formula list order
 * - formulaListAsc {Boolean}       - formula list ascending order
 * - productList {Array}            - array of product objects
 * - linearProductList {Array}      - array of objects for rendering of product list
 * - fandeckList {Array}            - array of fandeck objects
 * - linearFandeckList {Array}      - array of objects for rendering of fandeck list
 * - formulaList {Array}            - array of formula objects
 * - formulaCodeList {Array}        - array of formula code objects
 * - baseList {Array}               - array of base objects
 * - bippList {Array}               - array of bipp objects
 * - local {Boolean}                - if true, search panel contains data from local database
 * - refresh {String}               - if not null, refresh competent search panel lists
 * - reload {Boolean}               - if true, search panel requires reloading
 */
export const searchPanelReducer = (state = {}, action) => {
    switch (action.type) {
        case actionTypes.SEARCH_PANEL_INIT:
            let initState = {};
            initState[action.payload.tabId] = {
                ...initialState,
                type: searchPanelService.getSearchPanelType(action.payload.type),
                init: true,
                local: action.payload.local,
                formulaListOrder: window.storeProvider.getFormulaListOrder()
            };

            return {...state, ...initState};
        case actionTypes.CONSTRUCT_SEARCH_PANEL:
        case actionTypes.SET_SEARCH_PANEL:
            let initObject = {};
            initObject[action.payload.tabId] = {
                ...initialState,
                init: action.type === actionTypes.SET_SEARCH_PANEL,
                productNameInput: action.payload.product === null ? "" : action.payload.product.displayName,
                fandeckNameInput: action.payload.fandeck === null ? "" : action.payload.fandeck.displayName,
                formulaNameInput: action.payload.color === null ? "" : action.payload.color.displayName,
                formulaCodeInput: action.payload.color === null ? "" : action.payload.color.code,
                baseNameInput: action.payload.base === null ? "" : action.payload.base.displayName,
                bippNameInput: action.payload.bipp === null ? "" : action.payload.bipp.getLongDisplayName(),
                local: action.payload.color === null ? false : action.payload.color.local,
                bippList: Array.isArray(action.payload.bippList) ? action.payload.bippList : [],
                formulaListOrder: window.storeProvider.getFormulaListOrder()
            };

            return {...state, ...initObject};
        case actionTypes.UPDATE_FORMULA_FROM_HISTORICAL_FORMULA:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaNameInput: action.payload.formulaData.formula.color.displayName,
                    formulaCodeInput: action.payload.formulaData.formula.color.code,
                    baseNameInput: action.payload.formulaData.base.displayName,
                    bippNameInput: action.payload.formulaData.bipp?.getLongDisplayName() ?? ""
                },
                state
            );
        case actionTypes.UPDATE_SEARCH_PANEL:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    productNameInput: action.payload.productNameInput,
                    fandeckNameInput: action.payload.fandeckNameInput,
                    formulaNameInput: action.payload.formulaNameInput,
                    formulaCodeInput: action.payload.formulaCodeInput,
                    baseNameInput: action.payload.baseNameInput,
                    bippNameInput: action.payload.bippNameInput,
                    productNameFilter: action.payload.productNameFilter,
                    fandeckNameFilter: action.payload.fandeckNameFilter,
                    formulaNameFilter: action.payload.formulaNameFilter
                },
                state
            );
        case actionTypes.ADD_NEW_MAIN_TAB:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(action.payload.tabId, {type: componentTypes.SEARCH_PANEL_DEFAULT}, state);
        case actionTypes.SAVE_PRODUCT_LIST_SEARCH:
        case actionTypes.SAVE_PRODUCT_LIST_SEARCH_WITH_AUTOSELECT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            let productList = action.payload.productGroups
                ? modelHelper.mapProductsToObjects(action.payload.productList)
                : modelHelper.mapProductsWithoutGroupsToObjects(action.payload.productList);

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    productList: productList,
                    linearProductList: modelHelper.processTreeToLinearList(productList, "product").list
                },
                state
            );
        case actionTypes.SAVE_FANDECK_LIST_SEARCH:
        case actionTypes.SAVE_FANDECK_LIST_SEARCH_WITH_AUTOSELECT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            const fandeckList =
                action.payload.fandeckGroups && !state[action.payload.tabId].local
                    ? modelHelper.mapFandecksWithGroupsToObjects(action.payload.fandeckList)
                    : modelHelper.mapFandecksWithoutGroupsToObjects(action.payload.fandeckList);

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeckList: fandeckList,
                    linearFandeckList: modelHelper.processTreeToLinearList(fandeckList, "fandeck").list
                },
                state
            );
        case actionTypes.SAVE_FORMULA_LIST_SEARCH_INIT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            let initFormulaList = modelHelper.mapColorsToObjects(
                action.payload.colorList,
                state[action.payload.tabId].local
            );

            if (state[action.payload.tabId].formulaCodeList.length === 0) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {formulaList: initFormulaList, formulaCodeList: initFormulaList},
                    state
                );
            }

            return reducerHelper.getNewState(action.payload.tabId, {formulaList: initFormulaList}, state);
        case actionTypes.SAVE_FORMULA_CODE_LIST_SEARCH_INIT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaCodeList: modelHelper.mapColorsToObjects(
                        action.payload.colorList,
                        state[action.payload.tabId].local
                    )
                },
                state
            );
        case actionTypes.SAVE_FORMULA_LIST_SEARCH:
        case actionTypes.SAVE_FORMULA_LIST_SEARCH_WITH_AUTOSELECT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaList: modelHelper.mapColorsToObjects(
                        action.payload.colorList,
                        state[action.payload.tabId].local
                    )
                },
                state
            );
        case actionTypes.SAVE_FORMULA_CODE_LIST_SEARCH:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaCodeList: modelHelper.mapColorsToObjects(
                        action.payload.colorList,
                        state[action.payload.tabId].local
                    )
                },
                state
            );
        case actionTypes.SAVE_FORMULA_LIST_SEARCH_WITH_OFFSET:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaList: state[action.payload.tabId].formulaList.concat(
                        modelHelper.mapColorsToObjects(action.payload.colorList, state[action.payload.tabId].local)
                    )
                },
                state
            );
        case actionTypes.SAVE_FORMULA_CODE_LIST_SEARCH_WITH_OFFSET:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaCodeList: state[action.payload.tabId].formulaCodeList.concat(
                        modelHelper.mapColorsToObjects(action.payload.colorList, state[action.payload.tabId].local)
                    )
                },
                state
            );
        case actionTypes.SAVE_FANDECK_LIST_SEARCH_WITH_OFFSET:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            const fandeckOffsetList =
                action.payload.fandeckGroups && !state[action.payload.tabId].local
                    ? modelHelper.mapFandecksWithGroupsToObjects(action.payload.fandeckList)
                    : modelHelper.mapFandecksWithoutGroupsToObjects(action.payload.fandeckList);

            let newFandeckList = [...state[action.payload.tabId].fandeckList];

            for (let fandeckGroup of fandeckOffsetList) {
                let existingGroup = getterHelper.getObjectById(newFandeckList, fandeckGroup.id);

                if (existingGroup === null) {
                    newFandeckList.push(fandeckGroup);
                } else {
                    existingGroup.items = existingGroup.items.concat(fandeckGroup.items);
                }
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeckList: newFandeckList,
                    linearFandeckList: modelHelper.processTreeToLinearList(newFandeckList, "fandeck").list
                },
                state
            );
        case actionTypes.SAVE_BASE_LIST_SEARCH:
        case actionTypes.SAVE_BASE_LIST_SEARCH_WITH_AUTOSELECT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    baseList: modelHelper.mapBasesToObjects(action.payload.baseList)
                },
                state
            );
        case actionTypes.SAVE_BASE_LIST_SEARCH_WITH_OFFSET:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    baseList: state[action.payload.tabId].baseList.concat(
                        modelHelper.mapBasesToObjects(action.payload.baseList)
                    )
                },
                state
            );
        case actionTypes.SAVE_BIPP_LIST_SEARCH:
        case actionTypes.SAVE_BIPP_LIST_SEARCH_WITH_AUTOSELECT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    bippList: modelHelper.mapBippToObjects(action.payload.bippList)
                },
                state
            );
        case actionTypes.SAVE_BIPP_LIST_SEARCH_WITH_OFFSET:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    bippList: state[action.payload.tabId].bippList.concat(
                        modelHelper.mapBippToObjects(action.payload.bippList)
                    )
                },
                state
            );
        case actionTypes.SAVE_BROWSE_COLOR_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaListOffset: action.payload.formulaListOffset,
                    formulaList: action.payload.data
                },
                state
            );
        case actionTypes.SAVE_BROWSE_COLOR_LIST_WITH_OFFSET:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaList: state[action.payload.tabId].formulaList.concat(action.payload.data)
                },
                state
            );
        case actionTypes.SEARCH_PANEL_SET_ACTIVE_SELECT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeSelect: action.payload.activeSelect
                },
                state
            );
        case actionTypes.SEARCH_PANEL_SET_PRODUCT_NAME_INPUT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    productNameInput: action.payload.productName,
                    productNameFilter: action.payload.productName
                },
                state
            );
        case actionTypes.SELECT_PRODUCT:
            if (state[action.payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeSelect: "",
                        productNameInput: action.payload.product.displayName,
                        formulaNameInput: "",
                        formulaCodeInput: "",
                        productNameFilter: "",
                        formulaNameFilter: "",
                        fandeckListOffset: config.MAX_FANDECK_LIMIT,
                        baseListOffset: config.MAX_BASE_LIMIT,
                        fandeckList: [],
                        linearFandeckList: [],
                        formulaList: [],
                        formulaCodeList: [],
                        baseList: [],
                        refresh: actionTypes.SELECT_PRODUCT
                    },
                    state
                );
            }

            if (state[action.payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeSelect: "",
                        productNameInput: action.payload.product.displayName,
                        productNameFilter: action.payload.resetFilter
                            ? ""
                            : state[action.payload.tabId].productNameFilter,
                        refresh: actionTypes.SELECT_PRODUCT
                    },
                    state
                );
            }

            if (action.payload.bipp !== null && action.payload.bipp.product.id !== action.payload.product.id) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeSelect: "",
                        productNameInput: action.payload.product.displayName,
                        productNameFilter: action.payload.resetFilter
                            ? ""
                            : state[action.payload.tabId].productNameFilter,
                        bippNameInput: "",
                        fandeckListOffset: config.MAX_FANDECK_LIMIT,
                        formulaListOffset: config.MAX_FORMULA_LIMIT,
                        formulaCodeListOffset: config.MAX_FORMULA_LIMIT,
                        baseListOffset: config.MAX_BASE_LIMIT,
                        bippListOffset: config.MAX_BIPP_LIMIT,
                        fandeckList: [],
                        linearFandeckList: [],
                        formulaList: [],
                        formulaCodeList: [],
                        baseList: [],
                        bippList: [],
                        refresh: actionTypes.SELECT_PRODUCT
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeSelect: "",
                    productNameInput: action.payload.product.displayName,
                    productNameFilter: action.payload.resetFilter ? "" : state[action.payload.tabId].productNameFilter,
                    fandeckListOffset: config.MAX_FANDECK_LIMIT,
                    formulaListOffset: config.MAX_FORMULA_LIMIT,
                    formulaCodeListOffset: config.MAX_FORMULA_LIMIT,
                    baseListOffset: config.MAX_BASE_LIMIT,
                    fandeckList: [],
                    linearFandeckList: [],
                    formulaList: [],
                    formulaCodeList: [],
                    baseList: [],
                    refresh: actionTypes.SELECT_PRODUCT
                },
                state
            );
        case actionTypes.AUTOSELECT_PRODUCT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    productNameInput: action.payload.product.displayName,
                    refresh: null
                },
                state
            );
        case actionTypes.SEARCH_PANEL_SET_FANDECK_NAME_INPUT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeckNameInput: action.payload.fandeckName,
                    fandeckNameFilter: action.payload.fandeckName,
                    fandeckListOffset: config.MAX_FANDECK_LIMIT
                },
                state
            );
        case actionTypes.SELECT_FANDECK:
            if (state[action.payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeSelect: "",
                        fandeckNameInput: action.payload.fandeck.displayName,
                        formulaNameInput: "",
                        formulaCodeInput: "",
                        fandeckNameFilter: action.payload.resetFilter
                            ? ""
                            : state[action.payload.tabId].fandeckNameFilter,
                        formulaNameFilter: "",
                        productList: [],
                        linearProductList: [],
                        formulaList: [],
                        formulaCodeList: [],
                        baseList: [],
                        refresh: actionTypes.SELECT_FANDECK
                    },
                    state
                );
            }

            if (state[action.payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeSelect: "",
                        fandeckNameInput: action.payload.fandeck.displayName,
                        formulaListOffset: config.MAX_FORMULA_LIMIT,
                        formulaCodeListOffset: config.MAX_FORMULA_LIMIT,
                        formulaList: [],
                        formulaCodeList: [],
                        refresh: actionTypes.SELECT_FANDECK
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeSelect: "",
                    fandeckNameInput: action.payload.fandeck.displayName,
                    formulaListOffset: config.MAX_FORMULA_LIMIT,
                    formulaCodeListOffset: config.MAX_FORMULA_LIMIT,
                    baseListOffset: config.MAX_BASE_LIMIT,
                    productList: [],
                    linearProductList: [],
                    formulaList: [],
                    formulaCodeList: [],
                    baseList: [],
                    refresh: actionTypes.SELECT_FANDECK
                },
                state
            );
        case actionTypes.AUTOSELECT_FANDECK:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeckNameInput: action.payload.fandeck.displayName,
                    refresh: null
                },
                state
            );
        case actionTypes.SEARCH_PANEL_SET_FORMULA_INPUT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaNameInput: action.payload.formula,
                    formulaCodeInput: action.payload.formula,
                    formulaNameFilter: action.payload.formula,
                    formulaListOffset: config.MAX_FORMULA_LIMIT,
                    formulaCodeListOffset: config.MAX_FORMULA_LIMIT
                },
                state
            );
        case actionTypes.SEARCH_PANEL_SET_FORMULA_NAME_INPUT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaNameInput: action.payload.formulaName,
                    formulaNameFilter: action.payload.formulaName,
                    formulaListOffset: config.MAX_FORMULA_LIMIT
                },
                state
            );
        case actionTypes.SEARCH_PANEL_SET_FORMULA_CODE_INPUT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaCodeInput: action.payload.formulaCode,
                    formulaCodeListOffset: config.MAX_FORMULA_LIMIT
                },
                state
            );
        case actionTypes.SELECT_FORMULA:
            if (state[action.payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeSelect: "",
                        formulaNameInput: action.payload.color.displayName,
                        formulaCodeInput: action.payload.color.code === null ? "" : action.payload.color.code,
                        formulaNameFilter: "",
                        fandeckListOffset: config.MAX_FANDECK_LIMIT,
                        fandeckList: [],
                        linearFandeckList: [],
                        refresh: actionTypes.SELECT_FORMULA
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeSelect: "",
                    formulaNameInput: action.payload.color.displayName,
                    formulaCodeInput: action.payload.color.code === null ? "" : action.payload.color.code,
                    formulaNameFilter: "",
                    fandeckListOffset: config.MAX_FANDECK_LIMIT,
                    baseListOffset: config.MAX_BASE_LIMIT,
                    productList: [],
                    linearProductList: [],
                    fandeckList: [],
                    linearFandeckList: [],
                    baseList: [],
                    refresh: actionTypes.SELECT_FORMULA
                },
                state
            );
        case actionTypes.SELECT_FORMULA_NAME_AND_CODE:
        case actionTypes.SELECT_COLOR_PALETTE_FORMULA:
            if (state[action.payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeSelect: "",
                        formulaNameInput: action.payload.color.displayName,
                        formulaCodeInput: action.payload.color.code === null ? "" : action.payload.color.code,
                        formulaNameFilter: "",
                        refresh: actionTypes.SELECT_FORMULA
                    },
                    state
                );
            }

            if (state[action.payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeSelect: "",
                        formulaNameInput: action.payload.formulaNameInput,
                        formulaCodeInput: action.payload.color.code === null ? "" : action.payload.color.code,
                        formulaNameFilter: action.payload.resetFilter
                            ? ""
                            : state[action.payload.tabId].formulaNameFilter,
                        fandeckListOffset: config.MAX_FANDECK_LIMIT,
                        fandeckList: [],
                        linearFandeckList: [],
                        refresh: actionTypes.SELECT_FORMULA_NAME_AND_CODE
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeSelect: "",
                    formulaNameInput: action.payload.formulaNameInput,
                    formulaCodeInput: action.payload.color.code === null ? "" : action.payload.color.code,
                    formulaNameFilter: action.payload.resetFilter ? "" : state[action.payload.tabId].formulaNameFilter,
                    fandeckListOffset: config.MAX_FANDECK_LIMIT,
                    baseListOffset: config.MAX_BASE_LIMIT,
                    productList: [],
                    linearProductList: [],
                    fandeckList: [],
                    linearFandeckList: [],
                    baseList: [],
                    refresh: actionTypes.SELECT_FORMULA_NAME_AND_CODE
                },
                state
            );
        case actionTypes.AUTOSELECT_FORMULA:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaNameInput: action.payload.formulaNameInput,
                    formulaCodeInput: action.payload.color.code === null ? "" : action.payload.color.code,
                    refresh: null
                },
                state
            );
        case actionTypes.SEARCH_PANEL_SET_BASE_NAME_INPUT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    baseNameInput: action.payload.baseName,
                    baseListOffset: config.MAX_BASE_LIMIT
                },
                state
            );
        case actionTypes.SELECT_BASE:
            if (action.payload.bipp !== null && action.payload.bipp.base.id !== action.payload.base.id) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeSelect: "",
                        baseNameInput: action.payload.base.displayName,
                        bippNameInput: "",
                        fandeckListOffset: config.MAX_FANDECK_LIMIT,
                        formulaListOffset: config.MAX_FORMULA_LIMIT,
                        formulaCodeListOffset: config.MAX_FORMULA_LIMIT,
                        bippListOffset: config.MAX_BIPP_LIMIT,
                        fandeckList: [],
                        linearFandeckList: [],
                        formulaList: [],
                        formulaCodeList: [],
                        bippList: [],
                        refresh: actionTypes.SELECT_BASE
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeSelect: "",
                    baseNameInput: action.payload.base.displayName,
                    fandeckListOffset: config.MAX_FANDECK_LIMIT,
                    formulaListOffset: config.MAX_FORMULA_LIMIT,
                    formulaCodeListOffset: config.MAX_FORMULA_LIMIT,
                    fandeckList: [],
                    linearFandeckList: [],
                    formulaList: [],
                    formulaCodeList: [],
                    refresh: actionTypes.SELECT_BASE
                },
                state
            );
        case actionTypes.AUTOSELECT_BASE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    baseNameInput: action.payload.base.displayName,
                    refresh: null
                },
                state
            );
        case actionTypes.SEARCH_PANEL_SET_BIPP_NAME_INPUT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    bippNameInput: action.payload.bippName,
                    bippListOffset: config.MAX_BIPP_LIMIT
                },
                state
            );
        case actionTypes.SELECT_BIPP:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeSelect: "",
                    productNameInput: action.payload.bipp.product.displayName,
                    baseNameInput: action.payload.bipp.base.displayName,
                    bippNameInput: action.payload.bipp.getLongDisplayName(),
                    fandeckListOffset: config.MAX_FANDECK_LIMIT,
                    formulaListOffset: config.MAX_FORMULA_LIMIT,
                    formulaCodeListOffset: config.MAX_FORMULA_LIMIT,
                    baseListOffset: config.MAX_BASE_LIMIT,
                    fandeckList: [],
                    linearFandeckList: [],
                    formulaList: [],
                    formulaCodeList: [],
                    baseList: [],
                    refresh: actionTypes.SELECT_BIPP
                },
                state
            );
        case actionTypes.AUTOSELECT_BIPP:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    bippNameInput: action.payload.bipp.getLongDisplayName(),
                    refresh: null
                },
                state
            );
        case actionTypes.MAIN_TAB_SET_PACKAGE:
            if (state[action.payload.tabId] === undefined || action.payload.selectedPackage.package.isCustom()) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    bippNameInput: action.payload.selectedPackage.getLongDisplayName()
                },
                state
            );
        case actionTypes.SEARCH_PANEL_REMOVE_SELECTED:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            if (state[action.payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        ...initialState,
                        type: state[action.payload.tabId].type,
                        init: state[action.payload.tabId].init,
                        activeSelect: action.payload.panel,
                        productNameInput: state[action.payload.tabId].productNameInput,
                        fandeckNameInput: state[action.payload.tabId].fandeckNameInput,
                        formulaNameInput: state[action.payload.tabId].formulaNameInput,
                        formulaCodeInput: state[action.payload.tabId].formulaCodeInput,
                        local: state[action.payload.tabId].local
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    ...initialState,
                    type: state[action.payload.tabId].type,
                    init: state[action.payload.tabId].init,
                    activeSelect: action.payload.panel,
                    productNameInput: state[action.payload.tabId].productNameInput,
                    fandeckNameInput: state[action.payload.tabId].fandeckNameInput,
                    formulaNameInput: state[action.payload.tabId].formulaNameInput,
                    formulaCodeInput: state[action.payload.tabId].formulaCodeInput,
                    baseNameInput: state[action.payload.tabId].baseNameInput,
                    bippNameInput: state[action.payload.tabId].bippNameInput,
                    formulaListOrder: state[action.payload.tabId].formulaListOrder,
                    formulaListAsc: state[action.payload.tabId].formulaListAsc,
                    local: state[action.payload.tabId].local
                },
                state
            );
        case actionTypes.FILTER_PRODUCT_LIST_BY_NAME:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    productList: [],
                    linearProductList: []
                },
                state
            );
        case actionTypes.RELOAD_FANDECK_LIST_SEARCH:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeckListOffset: action.payload.fandeckOffset + config.MAX_FANDECK_LIMIT
                },
                state
            );
        case actionTypes.FILTER_FANDECK_LIST:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeckListOffset: config.MAX_FANDECK_LIMIT
                },
                state
            );
        case actionTypes.FILTER_FANDECK_LIST_BY_NAME:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeckList: [],
                    linearFandeckList: []
                },
                state
            );
        case actionTypes.RELOAD_FORMULA_LIST_SEARCH_BY_NAME:
        case actionTypes.RELOAD_FORMULA_LIST_SEARCH_BY_NAME_AND_CODE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaListOffset: action.payload.formulaOffset + action.payload.formulaLimit
                },
                state
            );
        case actionTypes.FILTER_FORMULA_LIST:
            if (state[action.payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaListOffset: config.MAX_FORMULA_LIMIT,
                    formulaCodeListOffset: config.MAX_FORMULA_LIMIT
                },
                state
            );
        case actionTypes.RELOAD_FORMULA_LIST_SEARCH_BY_CODE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaCodeListOffset: action.payload.formulaOffset + config.MAX_FORMULA_LIMIT
                },
                state
            );
        case actionTypes.FILTER_FORMULA_LIST_BY_NAME:
        case actionTypes.FILTER_FORMULA_LIST_BY_NAME_AND_CODE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaList: []
                },
                state
            );
        case actionTypes.FILTER_FORMULA_LIST_BY_CODE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaCodeList: []
                },
                state
            );
        case actionTypes.RELOAD_SEARCH_PANEL_COLOR_PALETTE:
            let tempState = state;

            for (let tabId in state) {
                if (state[tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                    tempState = reducerHelper.getNewState(
                        tabId,
                        {
                            formulaListOffset: 0
                        },
                        tempState
                    );
                }
            }

            return tempState;
        case actionTypes.RELOAD_BASE_LIST_SEARCH:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    baseListOffset: action.payload.baseOffset + config.MAX_BASE_LIMIT
                },
                state
            );
        case actionTypes.FILTER_BASE_LIST:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    baseListOffset: config.MAX_FANDECK_LIMIT
                },
                state
            );
        case actionTypes.FILTER_BASE_LIST_BY_NAME:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    baseList: []
                },
                state
            );
        case actionTypes.RELOAD_BIPP_LIST_SEARCH:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    bippListOffset: action.payload.bippOffset + config.MAX_BIPP_LIMIT
                },
                state
            );
        case actionTypes.FILTER_BIPP_LIST:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    bippListOffset: config.MAX_BIPP_LIMIT
                },
                state
            );
        case actionTypes.FILTER_BIPP_LIST_BY_NAME:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    bippList: []
                },
                state
            );
        case actionTypes.SEARCH_PANEL_TOGGLE_FORMULA_NAME_SORT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            if (state[action.payload.tabId].formulaListOrder !== null) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        formulaListOrder: null
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaListOrder: null,
                    formulaListAsc: !state[action.payload.tabId].formulaListAsc
                },
                state
            );
        case actionTypes.SEARCH_PANEL_TOGGLE_FORMULA_CODE_SORT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            if (state[action.payload.tabId].formulaListOrder !== "code") {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        formulaListOrder: "code"
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formulaListOrder: "code",
                    formulaListAsc: !state[action.payload.tabId].formulaListAsc
                },
                state
            );
        case actionTypes.SAVE_FORMULA_DATA:
            if (
                state[action.payload.tabId] === undefined ||
                action.payload.base === null ||
                action.payload.bipp === null
            ) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    baseNameInput: action.payload.base.displayName,
                    bippNameInput: action.payload.bipp.getLongDisplayName()
                },
                state
            );
        case actionTypes.SAVE_FORMULA_META_AND_PRINT_DATA:
        case actionTypes.SAVE_FORMULA_DATA_FROM_STATS:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    refresh: null
                },
                state
            );
        case actionTypes.INIT_QUICK_DISPENSING:
            if (state[componentTypes.QUICK_TINT_TAB_ID] === undefined) {
                return state;
            }

            let quickTintTabId = String(componentTypes.QUICK_TINT_TAB_ID);
            let newState = {};

            for (let tabId in state) {
                if (tabId !== quickTintTabId) {
                    newState[tabId] = state[tabId];
                }
            }

            return newState;
        case actionTypes.MAIN_TAB_RELOAD_REQUIRED:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    reload: true
                },
                state
            );
        case actionTypes.CLOSE_ACTIVE_TAB:
        case actionTypes.CLOSE_ALL_TABS_EXCEPT_ACTIVE:
        case actionTypes.CLOSE_ALL_TABS:
            let finalState = {};

            let tab;
            for (tab of action.payload.newTabList) {
                if (state[tab.id] !== undefined) {
                    finalState[tab.id] = state[tab.id];
                }
            }

            return finalState;
        case actionTypes.LOG_OUT_SUCCESS:
        case actionTypes.WEBSOCKET_CLOSED:
        case actionTypes.DATABASE_CHANGE:
            return {};
        default:
            return state;
    }
};
